import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Badge,
  Avatar,
  Spinner,
  Divider,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Location24Regular,
  Calendar24Regular,
  Document24Regular,
  Copy24Regular,
  Navigation24Regular,
  Call24Regular,
  Chat24Regular,
  Checkmark24Regular,
  Clock24Regular,
  VehicleCab24Regular,
  Box24Regular,
} from '@fluentui/react-icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { PortalOrder, TrackingEvent } from '../types';
import { categoryLabelForName } from '../productLabels';
import { formatCalendarDate, formatBusinessDateTime } from '../../lib/businessTime';

// Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_API_KEY || '';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    color: tokens.colorNeutralForeground2,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '1fr',
    },
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
  },
  cardHeader: {
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  cardContent: {
    ...shorthands.padding('20px'),
  },
  mapContainer: {
    height: '350px',
    ...shorthands.borderRadius('8px'),
    ...shorthands.overflow('hidden'),
  },
  mapInner: {
    width: '100%',
    height: '100%',
  },
  expandMapButton: {
    position: 'absolute',
    bottom: '16px',
    right: '16px',
    zIndex: 10,
  },
  etaCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('20px'),
    background: 'linear-gradient(135deg, #0078d4 0%, #004578 100%)',
    ...shorthands.borderRadius('8px'),
    color: '#ffffff',
  },
  etaIcon: {
    width: '48px',
    height: '48px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  etaContent: {
    flex: 1,
  },
  etaLabel: {
    fontSize: '14px',
    opacity: 0.9,
  },
  etaValue: {
    fontSize: '28px',
    fontWeight: '600',
  },
  driverCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
  },
  driverInfo: {
    flex: 1,
  },
  driverName: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  driverVehicle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  driverActions: {
    display: 'flex',
    gap: '8px',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
  },
  timelineItem: {
    display: 'flex',
    gap: '16px',
    position: 'relative',
  },
  timelineMarker: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '24px',
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralStroke1,
    ...shorthands.border('2px', 'solid', '#ffffff'),
    boxShadow: `0 0 0 2px ${tokens.colorNeutralStroke1}`,
    zIndex: 1,
  },
  timelineDotCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    boxShadow: `0 0 0 2px ${tokens.colorPaletteGreenBackground3}`,
  },
  timelineDotCurrent: {
    backgroundColor: tokens.colorBrandBackground,
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground}`,
    animation: 'pulse 2s infinite',
  },
  timelineLine: {
    width: '2px',
    flex: 1,
    backgroundColor: tokens.colorNeutralStroke1,
    minHeight: '40px',
  },
  timelineLineCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  timelineContent: {
    flex: 1,
    paddingBottom: '24px',
  },
  timelineTitle: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  timelineTitlePending: {
    color: tokens.colorNeutralForeground3,
  },
  timelineTime: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    ':last-child': {
      borderBottom: 'none',
    },
  },
  detailLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
    textAlign: 'right' as const,
  },
  productCategory: {
    fontSize: '12px',
    fontWeight: '400',
    color: tokens.colorNeutralForeground3,
    textAlign: 'right' as const,
  },
  addressValue: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  pricingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '0'),
  },
  pricingTotal: {
    fontWeight: '600',
    fontSize: '16px',
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
});

// Mock order data
const getMockOrder = (id: string): PortalOrder => ({
  id,
  orderNumber: '1234',
  status: 'in_transit',
  booqableStatus: 'picked_up',
  product: {
    id: 'prod-1',
    name: '30-yard Rolloff Dumpster',
    photoUrl: '/rolloff.png',
  },
  location: {
    address1: '123 Main Street',
    city: 'San Juan',
    region: 'PR',
    zipcode: '00901',
    coordinates: { lat: 18.4655, lng: -66.1057 },
  },
  startDate: '2025-12-10',
  endDate: '2025-12-17',
  pricing: {
    basePrice: 350,
    deliveryFee: 50,
    pickupFee: 0,
    tax: 32,
    total: 432,
    paid: 432,
    balance: 0,
  },
  tracking: {
    orderId: id,
    status: 'en_route',
    etaMinutes: 15,
    driverLocation: {
      lat: 18.4275,
      lng: -66.0614,
      lastUpdated: new Date().toISOString(),
    },
    driver: {
      id: 'driver-1',
      name: 'José García',
      truckId: 'truck-102',
      truckName: 'Truck 102',
    },
    timeline: [
      { id: '1', event: 'Order Placed', timestamp: '2025-12-09T14:00:00Z', description: 'Order confirmed', completed: true },
      { id: '2', event: 'Confirmed', timestamp: '2025-12-09T15:00:00Z', description: 'Payment received', completed: true },
      { id: '3', event: 'Dispatched', timestamp: '2025-12-10T07:00:00Z', description: 'Driver assigned', completed: true },
      { id: '4', event: 'In Transit', timestamp: '2025-12-10T08:00:00Z', description: 'ETA: 8:45am', completed: false },
      { id: '5', event: 'Delivered', timestamp: '', description: 'Pending', completed: false },
      { id: '6', event: 'Pickup Scheduled', timestamp: '2025-12-17T08:00:00Z', description: 'Dec 17', completed: false },
    ],
  },
  createdAt: '2025-12-09T14:00:00Z',
  updatedAt: '2025-12-10T08:00:00Z',
});

const OrderDetails: React.FC = () => {
  const styles = useStyles();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<PortalOrder | null>(null);

  useEffect(() => {
    const loadOrder = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setOrder(getMockOrder(orderId || ''));
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderId]);

  // Initialize map
  useEffect(() => {
    if (!order || !mapContainerRef.current || mapRef.current) return;

    const destination = order.location.coordinates;
    const driverLocation = order.tracking?.driverLocation;

    if (!destination) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [destination.lng, destination.lat],
      zoom: 12,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add destination marker
    new mapboxgl.Marker({ color: '#0078d4' })
      .setLngLat([destination.lng, destination.lat])
      .setPopup(new mapboxgl.Popup().setHTML('<strong>Delivery Location</strong><br/>123 Main Street'))
      .addTo(mapRef.current);

    // Add driver marker if en route
    if (driverLocation && order.tracking?.status === 'en_route') {
      new mapboxgl.Marker({ color: '#ff8c00' })
        .setLngLat([driverLocation.lng, driverLocation.lat])
        .setPopup(new mapboxgl.Popup().setHTML(`<strong>${order.tracking.driver?.name}</strong><br/>${order.tracking.driver?.truckName}`))
        .addTo(mapRef.current);

      // Fit bounds to show both markers
      mapRef.current.fitBounds([
        [Math.min(destination.lng, driverLocation.lng) - 0.02, Math.min(destination.lat, driverLocation.lat) - 0.02],
        [Math.max(destination.lng, driverLocation.lng) + 0.02, Math.max(destination.lat, driverLocation.lat) + 0.02],
      ]);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [order]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_transit':
        return <Badge appearance="filled" color="brand" size="large">In Transit</Badge>;
      case 'on_site':
        return <Badge appearance="filled" color="success" size="large">On Site</Badge>;
      case 'completed':
        return <Badge appearance="outline" color="success" size="large">Completed</Badge>;
      default:
        return <Badge appearance="outline" size="large">{status}</Badge>;
    }
  };

  // Rental period: date-only calendar value anchored at midnight UTC.
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Pending';
    return formatCalendarDate(dateString);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading order details..." />
      </div>
    );
  }

  if (!order) {
    return <div>Order not found</div>;
  }

  const currentTimelineIndex = order.tracking?.timeline.findIndex(t => !t.completed) ?? -1;

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          className={styles.backButton}
          onClick={() => navigate('/portal/orders')}
        />
        <div className={styles.headerContent}>
          <Text className={styles.headerTitle}>
            Order #{order.orderNumber}
            {getStatusBadge(order.status)}
          </Text>
        </div>
        <div className={styles.headerActions}>
          <Button appearance="subtle" icon={<Chat24Regular />}>
            Message
          </Button>
          <Button appearance="subtle" icon={<Call24Regular />}>
            Call Support
          </Button>
        </div>
      </div>

      <div className={styles.grid}>
        {/* Main Column */}
        <div className={styles.mainColumn}>
          {/* Map */}
          <div className={styles.card}>
            <div className={styles.mapContainer} style={{ position: 'relative' }}>
              <div ref={mapContainerRef} className={styles.mapInner} />
              {order.tracking?.status === 'en_route' && (
                <Button
                  appearance="primary"
                  className={styles.expandMapButton}
                  onClick={() => navigate(`/portal/tracking/${order.id}`)}
                >
                  Track Live
                </Button>
              )}
            </div>
          </div>

          {/* ETA Card (if in transit) */}
          {order.tracking?.status === 'en_route' && order.tracking.etaMinutes && (
            <div className={styles.etaCard}>
              <div className={styles.etaIcon}>
                <VehicleCab24Regular style={{ fontSize: '24px' }} />
              </div>
              <div className={styles.etaContent}>
                <Text className={styles.etaLabel}>Estimated Arrival</Text>
                <Text className={styles.etaValue}>{order.tracking.etaMinutes} minutes</Text>
              </div>
            </div>
          )}

          {/* Driver Card (if in transit) */}
          {order.tracking?.driver && order.tracking?.status === 'en_route' && (
            <div className={styles.driverCard}>
              <Avatar name={order.tracking.driver.name} size={48} color="colorful" />
              <div className={styles.driverInfo}>
                <Text className={styles.driverName}>{order.tracking.driver.name}</Text>
                <Text className={styles.driverVehicle}>{order.tracking.driver.truckName}</Text>
              </div>
              <div className={styles.driverActions}>
                <Button appearance="subtle" icon={<Call24Regular />} />
                <Button appearance="subtle" icon={<Chat24Regular />} />
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Text className={styles.cardTitle}>Tracking Timeline</Text>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.timeline}>
                {order.tracking?.timeline.map((event, index) => {
                  const isCurrent = index === currentTimelineIndex;
                  const isLast = index === order.tracking!.timeline.length - 1;

                  return (
                    <div key={event.id} className={styles.timelineItem}>
                      <div className={styles.timelineMarker}>
                        <div
                          className={`${styles.timelineDot} ${
                            event.completed ? styles.timelineDotCompleted :
                            isCurrent ? styles.timelineDotCurrent : ''
                          }`}
                        />
                        {!isLast && (
                          <div
                            className={`${styles.timelineLine} ${
                              event.completed ? styles.timelineLineCompleted : ''
                            }`}
                          />
                        )}
                      </div>
                      <div className={styles.timelineContent}>
                        <Text
                          className={`${styles.timelineTitle} ${
                            !event.completed && !isCurrent ? styles.timelineTitlePending : ''
                          }`}
                        >
                          {event.event}
                        </Text>
                        <Text className={styles.timelineTime}>
                          {event.timestamp ? formatBusinessDateTime(event.timestamp) : event.description}
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Side Column */}
        <div className={styles.sideColumn}>
          {/* Order Details */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Text className={styles.cardTitle}>Order Details</Text>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Product</Text>
                <div>
                  <Text className={styles.detailValue} block>{order.product.name}</Text>
                  {categoryLabelForName(order.product.name) && (
                    <Text className={styles.productCategory} block>
                      {categoryLabelForName(order.product.name)}
                    </Text>
                  )}
                </div>
              </div>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Delivery Address</Text>
                <div className={styles.addressValue}>
                  <Text className={styles.detailValue}>
                    {order.location.address1}<br />
                    {order.location.city}, {order.location.region} {order.location.zipcode}
                  </Text>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Copy24Regular />}
                    onClick={() => copyToClipboard(`${order.location.address1}, ${order.location.city}, ${order.location.region} ${order.location.zipcode}`)}
                  />
                </div>
              </div>
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Rental Period</Text>
                <Text className={styles.detailValue}>
                  {formatDate(order.startDate)} → {formatDate(order.endDate)}
                </Text>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Text className={styles.cardTitle}>Pricing</Text>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.pricingRow}>
                <Text className={styles.detailLabel}>Base Rental</Text>
                <Text>${order.pricing.basePrice.toFixed(2)}</Text>
              </div>
              <div className={styles.pricingRow}>
                <Text className={styles.detailLabel}>Delivery</Text>
                <Text>${order.pricing.deliveryFee.toFixed(2)}</Text>
              </div>
              {order.pricing.pickupFee > 0 && (
                <div className={styles.pricingRow}>
                  <Text className={styles.detailLabel}>Pickup</Text>
                  <Text>${order.pricing.pickupFee.toFixed(2)}</Text>
                </div>
              )}
              <div className={styles.pricingRow}>
                <Text className={styles.detailLabel}>Tax</Text>
                <Text>${order.pricing.tax.toFixed(2)}</Text>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <div className={styles.pricingRow}>
                <Text className={styles.pricingTotal}>Total</Text>
                <Text className={styles.pricingTotal}>${order.pricing.total.toFixed(2)}</Text>
              </div>
              <div className={styles.pricingRow}>
                <Text className={styles.detailLabel}>Paid</Text>
                <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>
                  ${order.pricing.paid.toFixed(2)}
                </Text>
              </div>
              {order.pricing.balance > 0 && (
                <div className={styles.pricingRow}>
                  <Text className={styles.detailLabel}>Balance Due</Text>
                  <Text style={{ color: tokens.colorPaletteRedForeground1, fontWeight: '600' }}>
                    ${order.pricing.balance.toFixed(2)}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Documents */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Text className={styles.cardTitle}>Documents</Text>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.documentItem}>
                <Document24Regular />
                <Text>Invoice #INV-{order.orderNumber}</Text>
              </div>
              <div className={styles.documentItem}>
                <Document24Regular />
                <Text>Rental Agreement</Text>
              </div>
              <div className={styles.documentItem}>
                <Document24Regular />
                <Text>Delivery Confirmation</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
